import styled from '@emotion/styled'

import type { FC } from 'react'

import { css, useTheme } from '@emotion/react'

import sanitizeHtml from 'sanitize-html'

import Image from '@segment/matcha/components/Image'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import type { KeyFinding } from './data'
import DividerSection from './DividerSection'

interface KeyFindingSectionProps {
  keyFinding: KeyFinding
  isLast?: boolean
}

const mobileImageDimensions = {
  width: 327,
  height: 426,
}

const desktopImageDimensions = {
  width: 640,
  height: 280,
}

const KeyFindingSection: FC<KeyFindingSectionProps> = ({
  keyFinding: {
    heading,
    description,
    dataVisalization: { mobile, desktop, alt },
  },
  isLast = false,
}) => {
  const theme = useTheme()
  return (
    <>
      <StyledSectionBase variant="2Col" isLast={isLast}>
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Heading variant="h2" tag="h2">
              {heading}
            </CopyText.Heading>
            {description.map((paragraph, index) => (
              <Description
                key={index}
                variant="pLarge"
                tag="p"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(paragraph, { allowedTags: [`a`] }) }}
              />
            ))}
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <picture>
            <source
              type="image/webp"
              media={theme.breakpoints.up(`sm`, false)}
              srcSet={`${desktop.webpSrc}, ${desktop.webp2xSrc} 2x`}
              {...desktopImageDimensions}
            />
            <source
              type="image/png"
              media={theme.breakpoints.up(`sm`, false)}
              srcSet={`${desktop.pngSrc}, ${desktop.png2xSrc} 2x`}
              {...desktopImageDimensions}
            />
            <source type="image/webp" srcSet={`${mobile.webpSrc}, ${mobile.webp2xSrc} 2x`} {...mobileImageDimensions} />

            <Image {...mobileImageDimensions} src={mobile.png2xSrc} alt={alt} loading="lazy" />
          </picture>
        </Section.Contents>
      </StyledSectionBase>
      {!isLast && <DividerSection />}
    </>
  )
}

const StyledSectionBase = styled(SectionBase)<Pick<KeyFindingSectionProps, `isLast`>>`
  ${Section.Wrapper} {
    ${({ isLast }) =>
      isLast &&
      css`
        padding-bottom: 80px;
      `}

    ${({ theme }) => theme.breakpoints.up(`xl`)} {
      column-gap: 80px;
    }

    ${({ theme }) => theme.breakpoints.up(`xxl`)} {
      grid-template-columns: min(560px, 50%) min(640px, 50%);
    }
  }
`

const Description = styled(CopyText.Description)`
  &:not(:first-of-type) {
    margin-top: 32px;
  }
`

export default KeyFindingSection
